import { IBookingDispute, IDisputeOffer, DisputeStatus } from '../interfaces/heavyDutyMachinery/IBookingDispute';
import { TRANSLATIONS } from '../constants/translations';
import { PillTone } from '../theme/creamyTokens';

/**
 * The one thing THIS viewer can do about this dispute right now.
 *
 * Same first-match-wins, exactly-one-action shape as
 * BookingLifecycleActions: a dispute is a thing two people take turns at,
 * and at any moment the turn belongs to one of them. 'none' is a real and
 * common answer -- most of the time you are waiting on the other side, and
 * the honest thing to show then is the deadline, not a button.
 */
export type DisputeAction = 'respond' | 'offer' | 'acceptOffer' | 'escalate' | 'withdraw' | 'none';

const LIVE_STATUSES: DisputeStatus[] = ['Open', 'Answered', 'UnderReview'];

/** Whether anything about this dispute can still change. */
export const isDisputeLive = (dispute: IBookingDispute) => LIVE_STATUSES.includes(dispute.status);

export const isClaimant = (dispute: IBookingDispute, userId?: string) =>
  !!userId && dispute.claimantUserId === userId;

/** The most recent amount on the table, whoever put it there. */
export const latestOffer = (dispute: IBookingDispute): IDisputeOffer | undefined =>
  dispute.offers.length === 0
    ? undefined
    : dispute.offers.reduce((latest, offer) => (offer.versionNumber > latest.versionNumber ? offer : latest));

/**
 * True when the amount at stake is below the line where this platform puts
 * a person on it -- which is not meanness, it's arithmetic: reviewing a
 * disagreement properly costs more than a small one is worth, and pretending
 * otherwise produces a slow decision that convinces nobody. Below the line,
 * no agreement means an even split.
 */
export const isBelowReviewThreshold = (dispute: IBookingDispute) =>
  dispute.claimedAmount < dispute.autoResolveThreshold;

/**
 * Whole hours left to answer, floored, or undefined when no clock is
 * running. Deliberately hours rather than a live countdown: this is a
 * five-day window on a phone that lives in a cab, and a ticking timer would
 * imply a precision the deadline sweep doesn't have.
 */
export const hoursToRespond = (dispute: IBookingDispute, now: Date = new Date()): number | undefined => {
  if (!dispute.respondByUtc || dispute.status !== 'Open') return undefined;
  const remaining = new Date(dispute.respondByUtc).getTime() - now.getTime();
  return remaining <= 0 ? 0 : Math.floor(remaining / 3_600_000);
};

/**
 * What happens if nobody touches this. Shown verbatim to both parties,
 * because it is the mechanism that actually resolves most disputes: a
 * deadline you can read is a deadline you act on, and a default you were
 * warned about is one you can't reasonably complain about afterwards.
 */
export const defaultOutcomeKey = (dispute: IBookingDispute, userId?: string): string | undefined => {
  if (dispute.status === 'Open') {
    return isClaimant(dispute, userId)
      ? TRANSLATIONS.DISPUTE_DEFAULT_SILENCE_CLAIMANT
      : TRANSLATIONS.DISPUTE_DEFAULT_SILENCE_RESPONDENT;
  }
  if (dispute.status === 'Answered') {
    return isBelowReviewThreshold(dispute)
      ? TRANSLATIONS.DISPUTE_DEFAULT_SPLIT
      : TRANSLATIONS.DISPUTE_DEFAULT_REVIEW;
  }
  return undefined;
};

export const nextDisputeAction = (dispute: IBookingDispute, userId?: string): DisputeAction => {
  if (!userId || !isDisputeLive(dispute)) return 'none';
  const mine = isClaimant(dispute, userId);

  // The respondent's turn, and the only thing that stops the clock.
  if (dispute.status === 'Open') return mine ? 'withdraw' : 'respond';

  if (dispute.status === 'Answered') {
    const last = latestOffer(dispute);
    // An offer is the other side's to accept, never your own -- otherwise
    // "accept" would just mean "insist", which is what the counter-offer
    // is already for.
    if (last && last.offeredByUserId !== userId) return 'acceptOffer';
    // Once you've put a number up and they haven't taken it, you need a way
    // to say "we're not getting there" -- there is no clock running on a
    // negotiation, so nothing else would ever end it. What that does
    // depends on the amount (a reviewer, or the split rule); see
    // escalateLabelKey.
    if (last) return 'escalate';
    return 'offer';
  }

  // UnderReview: it's out of both parties' hands, and saying so plainly
  // beats a disabled button.
  return 'none';
};

export const DISPUTE_STATUS_TONES: Record<DisputeStatus, PillTone> = {
  Open: 'warning',
  Answered: 'warning',
  UnderReview: 'warning',
  Settled: 'success',
  AutoResolved: 'default',
  Resolved: 'default',
  Withdrawn: 'default',
};

export const DISPUTE_STATUS_LABEL_KEYS: Record<DisputeStatus, string> = {
  Open: TRANSLATIONS.DISPUTE_STATUS_OPEN,
  Answered: TRANSLATIONS.DISPUTE_STATUS_ANSWERED,
  UnderReview: TRANSLATIONS.DISPUTE_STATUS_UNDER_REVIEW,
  Settled: TRANSLATIONS.DISPUTE_STATUS_SETTLED,
  AutoResolved: TRANSLATIONS.DISPUTE_STATUS_AUTO_RESOLVED,
  Resolved: TRANSLATIONS.DISPUTE_STATUS_RESOLVED,
  Withdrawn: TRANSLATIONS.DISPUTE_STATUS_WITHDRAWN,
};

/**
 * What "we're not getting there" actually does, which is not the same
 * button in both cases: above the threshold it hands the claim to a
 * reviewer, below it the split rule fires immediately and closes it. Same
 * decision, very different consequence -- so the label has to say which.
 */
export const escalateLabelKey = (dispute: IBookingDispute): string =>
  isBelowReviewThreshold(dispute) ? TRANSLATIONS.DISPUTE_ACTION_SPLIT : TRANSLATIONS.DISPUTE_ACTION_ESCALATE;

export const DISPUTE_ACTION_LABEL_KEYS: Record<Exclude<DisputeAction, 'none'>, string> = {
  respond: TRANSLATIONS.DISPUTE_ACTION_RESPOND,
  offer: TRANSLATIONS.DISPUTE_ACTION_OFFER,
  acceptOffer: TRANSLATIONS.DISPUTE_ACTION_ACCEPT_OFFER,
  escalate: TRANSLATIONS.DISPUTE_ACTION_ESCALATE,
  withdraw: TRANSLATIONS.DISPUTE_ACTION_WITHDRAW,
};
